import { useEffect, useRef, useState } from "react";

import { useKeyboardControls, useTexture } from "@react-three/drei";
import { RigidBody, useRapier } from "@react-three/rapier";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import useGame from "@stores/useGame";

export function Player() {
    const bodyRef = useRef();

    const textureBall = useTexture("./basketball.png");

    const cameraPosition = new THREE.Vector3();
    const cameraTarget = new THREE.Vector3();

    const enableOrbitControls = useGame((state) => state.enableOrbitControls);
    const blocksCount = useGame((state) => state.blocksCount);
    const blockSize = useGame((state) => state.blockSize);
    const restart = useGame((state) => state.restart);
    const start = useGame((state) => state.start);
    const phase = useGame((state) => state.phase);
    const end = useGame((state) => state.end);

    const [subscribeKeys, getKeys] = useKeyboardControls();
    const { rapier, world } = useRapier();

    const [smoothedCameraPosition] = useState(() => new THREE.Vector3(10, 10, 10))
    const [smoothedCameraTarget] = useState(() => new THREE.Vector3())

    const jump = () => {
        const origin = bodyRef.current.translation();
        origin.y -= 0.31;

        const direction = { x: 0, y: -1, z: 0 };
        const ray = new rapier.Ray(origin, direction);
        const hit = world.castRay(ray, 10, true);

        if (hit.toi < 0.15) {
            bodyRef.current.applyImpulse({ x: 0, y: 0.5, z: 0 })
        }
    }

    const reset = () => {
        if (bodyRef.current) {
            bodyRef.current.setTranslation({ x: 0, y: 0, z: 0 });
            bodyRef.current.setLinvel({ x: 0, y: 0, z: 0 });
            bodyRef.current.setAngvel({ x: 0, y: 0, z: 0 });
        }
    }

    useEffect(() => {
        const destroyReset = useGame.subscribe(
            (state) => state.phase,
            (value) => {
                if (value === "ready") {
                    reset();
                }
            }
        );

        const destroyJump = subscribeKeys(
            (state) => state.jump,
            (value) => {
                if (value) jump()
            }
        )

        return () => {
            destroyReset();
            destroyJump();
        }
    }, []);

    useFrame((state, delta) => {
        /**
         * Controls
         */
        const { forward, backward, leftward, rightward } = getKeys();

        if (phase === 'ready' && (forward || backward || leftward || rightward)) start();

        const impulse = { x: 0, y: 0, z: 0 }
        const torque = { x: 0, y: 0, z: 0 }

        const impulseStrength = 0.5 * delta;
        const torqueStrength = 0.15 * delta;

        if (forward) {
            impulse.z -= impulseStrength;
            torque.x -= torqueStrength;
        }

        if (rightward) {
            impulse.x += impulseStrength;
            torque.z -= torqueStrength;
        }

        if (backward) {
            impulse.z += impulseStrength;
            torque.x += torqueStrength;
        }

        if (leftward) {
            impulse.x -= impulseStrength;
            torque.z += torqueStrength;
        }

        if (bodyRef.current) {
            bodyRef.current.applyImpulse(impulse);
            bodyRef.current.applyTorqueImpulse(torque);

            const bodyPosition = bodyRef.current.translation()

            /**
             * Camera
             */
            if (!enableOrbitControls) {
                cameraPosition.copy(bodyPosition);
                cameraPosition.z += 2.25;
                cameraPosition.y += 0.65;
    
                cameraTarget.copy(bodyPosition);
                cameraTarget.y += 0.25;
    
                smoothedCameraPosition.lerp(cameraPosition, 5 * delta);
                smoothedCameraTarget.lerp(cameraTarget, 5 * delta);
    
                state.camera.position.copy(smoothedCameraPosition);
                state.camera.lookAt(smoothedCameraTarget);
            }

            /**
             * Phases
             */

            /**
             * If the ball reaches the final block
             */
            if (bodyPosition.z < - (blocksCount * blockSize + 2)) {
                end();
            }

            /**
             * If the ball falls
             */
            if (bodyPosition.y < - blockSize && !(bodyPosition.z < - (blocksCount * blockSize + 2))) {
                restart();
            }

            if (bodyPosition.y < - blockSize) {
                restart();
            }
        }

    });

    return (
        <RigidBody
            ref={bodyRef}
            canSleep={false}
            colliders="ball"
            restitution={0.2}
            friction={1}
            position={[0, 1, 0]}
            linearDamping={0.5}
            angularDamping={0.5}
        >
            <mesh scale={0.3} castShadow>
                <sphereGeometry />
                <meshStandardMaterial map={textureBall} />
            </mesh>
        </RigidBody>
    )
}